/* eslint-disable @typescript-eslint/no-explicit-any */
// Detail screen for a No-Dues-only separation case (termination / death /
// deputation / retirement / transfer-out). These cases have NO resignation form
// and NO approval hierarchy — only the No-Dues flow — so this screen shows the
// case itself, the submitted No-Dues form, the department clearances, the
// Establishment (settlement) stage and the activity trail. Nothing else.
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import {
  PageHeader,
  SectionCard,
  FieldLabel,
  ReadOnlyField,
  StatusBadge,
  WarningBanner,
  DepartmentClearanceCard,
  EmptyState,
} from "./shared/components";
import { NoDuesFormSections } from "./shared/NoDuesFormSections";
import {
  getResignationDetails,
  getNoDuesFormDetails,
  getNoDuesDispatchList,
  getNoDuesSettlementStatus,
  formatDate,
  formatDateTime,
  formatINR,
  formatSeparationMode,
  NoDuesDispatchRow,
  NoDuesSettlementStatus,
} from "./services/eSeparation.service";

const DISPATCH_UI_STATUS: Record<string, "Cleared" | "Pending" | "Dues Found"> = {
  CLEARED: "Cleared",
  PENDING: "Pending",
  REJECTED: "Dues Found",
};

// The four stages of the No-Dues-only flow, in order.
const STAGES = [
  { key: "form", label: "No-Dues Form", icon: "ti-pencil" },
  { key: "dept", label: "Department Clearance", icon: "ti-building" },
  { key: "estt", label: "Establishment", icon: "ti-cash" },
  { key: "done", label: "Completed", icon: "ti-circle-check" },
];

const ACTION_LABELS: Record<string, string> = {
  SUBMIT: "Case initiated / No-Dues submitted",
  DISPATCH: "Sent to departments",
  DEPT_CLEAR: "Department cleared",
  DEPT_REJECT: "Department found dues",
  SETTLE_DISPATCH: "Sent to Establishment",
  SETTLE_ITEM: "Establishment settled an item",
  SETTLE_CLOSE: "Establishment closed the settlement",
  RELEASE: "Documents released",
  AUTO_FORWARD: "System",
  DELEGATE: "Delegated to sub-nodal",
  REVOKE_DELEGATION: "Delegation revoked",
};

const NoDuesCaseDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const routes = all_routes;

  const state = (location.state as any) || {};
  const caseId: string = state.request_id || (params.id ? decodeURIComponent(params.id) : "");
  const viewerRole: string = state.role || "Employee";
  const isHr = viewerRole === "HR";

  const [loading, setLoading] = useState(true);
  const [kase, setKase] = useState<any>(null);
  const [ndRecord, setNdRecord] = useState<any>(null);
  const [dispatches, setDispatches] = useState<NoDuesDispatchRow[]>([]);
  const [settlement, setSettlement] = useState<NoDuesSettlementStatus | null>(null);
  const [activity, setActivity] = useState<any[]>([]);

  const load = useCallback(async () => {
    if (!caseId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const [caseRes, ndRes] = await Promise.all([
      getResignationDetails(caseId),
      getNoDuesFormDetails(caseId),
    ]);
    const parent = caseRes.data || null;
    const nd = ndRes.data || null;
    setKase(parent);
    setNdRecord(nd);

    const childId: string | null = nd?.nodues_request_id || null;
    let trail: any[] = Array.isArray(parent?.history) ? [...parent.history] : [];
    if (childId) {
      const [dRes, sRes, childRes] = await Promise.all([
        getNoDuesDispatchList(childId),
        getNoDuesSettlementStatus(childId),
        getResignationDetails(childId),
      ]);
      setDispatches(dRes.data || []);
      setSettlement(sRes.data || null);
      if (Array.isArray(childRes.data?.history)) trail = trail.concat(childRes.data.history);
    } else {
      setDispatches([]);
      setSettlement(null);
    }
    trail.sort(
      (a, b) => new Date(a.acted_at || 0).getTime() - new Date(b.acted_at || 0).getTime()
    );
    setActivity(trail);
    setLoading(false);
  }, [caseId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="text-muted mt-3 mb-0">Loading case…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!kase) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <PageHeader title="Separation Case" onExit={() => navigate(-1)} />
          <EmptyState icon="ti-file-off" title="Case not found" message="This case could not be loaded." />
        </div>
      </div>
    );
  }

  const childId: string | null = ndRecord?.nodues_request_id || null;
  const formSubmitted = !!childId || ndRecord?.status === "SUBMITTED";
  const displayStatus: string = kase.display_status || kase.status;
  const stage = (() => {
    if (displayStatus === "COMPLETED") return 3;
    if (displayStatus === "NO_DUES_ESTABLISHMENT") return 2;
    if (displayStatus === "NO_DUES_IN_PROGRESS") return dispatches.length ? 1 : 0.5;
    return 0;
  })();
  const clearedCount = dispatches.filter((d) => String(d.status).toUpperCase() === "CLEARED").length;
  const actedCount = dispatches.filter((d) => String(d.status).toUpperCase() !== "PENDING").length;
  const caseLabel = formatSeparationMode(kase.request_type);
  const initiatedBy =
    activity.find((a) => String(a.action).toUpperCase() === "SUBMIT" && !String(a.request_id || "").startsWith("ND-"));
  const initiatedByEmployee =
    !!initiatedBy &&
    String(initiatedBy.approver_emp_id || "").replace(/^0+/, "") ===
      String(kase.emp_no || "").replace(/^0+/, "");

  return (
    <div className="page-wrapper">
      <div className="content pb-5">
        <PageHeader
          title={`${caseLabel} Case`}
          subtitle={`${kase.request_id} · ${kase.emp_name || ""} (${kase.emp_no})`}
          right={<StatusBadge status={displayStatus} />}
          onExit={() => navigate(-1)}
        />

        <WarningBanner
          type="info"
          message="This is a No-Dues-only separation. There is no resignation form and no approval hierarchy — the case moves only through the No-Dues flow: the No-Dues form, department clearance, Establishment, and completion."
        />

        {/* Stage tracker */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16 }}>
          <div className="card-body">
            <div className="row g-2">
              {STAGES.map((s, i) => {
                const done = stage > i || (displayStatus === "COMPLETED" && i === 3);
                const current = !done && Math.ceil(stage) === i;
                const color = done ? "#049123" : current ? "#BA7517" : "#9aa0a6";
                return (
                  <div className="col-6 col-md-3" key={s.key}>
                    <div
                      className="d-flex align-items-center gap-2 p-2 rounded-3"
                      style={{
                        border: `1px solid ${done || current ? color : "rgba(0,0,0,0.08)"}`,
                        background: done ? "#E4F7E8" : current ? "#FFF8EC" : "#fff",
                      }}
                    >
                      <i className={`ti ${done ? "ti-circle-check" : s.icon}`} style={{ color, fontSize: 20 }} />
                      <div>
                        <div className="small fw-semibold" style={{ color }}>
                          {s.label}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {s.key === "form" && (formSubmitted ? "Submitted" : "Pending")}
                          {s.key === "dept" &&
                            (dispatches.length ? `${clearedCount}/${dispatches.length} cleared` : "Not started")}
                          {s.key === "estt" &&
                            (settlement?.settlement
                              ? settlement.settlement.status === "CLOSED"
                                ? "Closed"
                                : "In progress"
                              : "Not started")}
                          {s.key === "done" && (displayStatus === "COMPLETED" ? "Done" : "Pending")}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* What the viewer can do next */}
            <div className="d-flex flex-wrap gap-2 mt-3">
              {!formSubmitted && (isHr || (initiatedByEmployee && !isHr)) && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() =>
                    navigate(routes.Esepration.noDuesForm, {
                      state: { request_id: kase.request_id, hrFill: isHr },
                    })
                  }
                >
                  <i className="ti ti-pencil-plus me-1" />
                  Fill No-Dues Form
                </button>
              )}
              {isHr && childId && displayStatus !== "COMPLETED" && (
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => navigate(routes.Esepration.noDuesDispatch, { state: { request_id: childId } })}
                >
                  <i className="ti ti-send me-1" />
                  Open No-Dues (departments &amp; Establishment)
                </button>
              )}
              {isHr && displayStatus === "COMPLETED" && (
                <button
                  className="btn btn-success btn-sm"
                  onClick={() =>
                    navigate(`/e-sepration/no-dues/hr-review/${kase.request_id}`, {
                      state: { request_id: kase.request_id, emp_no: kase.emp_no, role: "HR" },
                    })
                  }
                >
                  <i className="ti ti-file-certificate me-1" />
                  Final Review &amp; Documents
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Case details */}
        <SectionCard index={0} title="Case Details">
          <div className="row g-3">
            <div className="col-md-6">
              <FieldLabel label="Type of Separation" />
              <ReadOnlyField value={caseLabel} />
            </div>
            <div className="col-md-6">
              <FieldLabel label="Initiated By" />
              <ReadOnlyField value={initiatedBy ? (initiatedByEmployee ? "Employee" : "HR") : "—"} />
            </div>
            <div className="col-md-6">
              <FieldLabel label="Employee" />
              <ReadOnlyField value={`${kase.emp_name || "—"} (${kase.emp_no})`} />
            </div>
            <div className="col-md-6">
              <FieldLabel label="Designation" />
              <ReadOnlyField value={kase.designation_text || kase.designation} />
            </div>
            <div className="col-md-6">
              <FieldLabel label="Department / Posting" />
              <ReadOnlyField value={kase.posting} />
            </div>
            <div className="col-md-6">
              <FieldLabel label="Date of Joining DMRC" />
              <ReadOnlyField value={kase.doj} />
            </div>
            <div className="col-md-6">
              <FieldLabel label="Date of Leaving" />
              <ReadOnlyField value={kase.relieved_wef || kase.desired_relieve_date} />
            </div>
            <div className="col-md-6">
              <FieldLabel label="Initiated On" />
              <ReadOnlyField value={kase.intimation_date} />
            </div>
            <div className="col-12">
              <FieldLabel label="Reason" />
              <div className="p-3 rounded-3 bg-light border" style={{ whiteSpace: "pre-wrap" }}>
                {kase.reason || "—"}
              </div>
            </div>
          </div>
        </SectionCard>

        {/* Department clearance */}
        <SectionCard index={1} title="Department Clearance">
          {!dispatches.length ? (
            <WarningBanner
              type="info"
              message={
                formSubmitted
                  ? "The No-Dues has not been sent to the departments yet."
                  : "The No-Dues form has not been submitted yet. Once it is, it goes to the departments for clearance."
              }
            />
          ) : (
            <>
              <div className="small text-muted mb-2">
                {actedCount} of {dispatches.length} departments have acted · {clearedCount} cleared
              </div>
              {dispatches.map((d) => (
                <DepartmentClearanceCard
                  key={d.id}
                  deptName={d.department_name}
                  nodalOfficer={d.approver_emp_id || "—"}
                  status={DISPATCH_UI_STATUS[String(d.status).toUpperCase()] || "Pending"}
                  remarks={d.remarks || ""}
                  recoveryAmount={d.recovery_amount ? Number(d.recovery_amount) : 0}
                  delegatedTo={d.delegated_to_emp_id || undefined}
                />
              ))}
            </>
          )}
        </SectionCard>

        {/* Establishment */}
        <SectionCard index={2} title="Establishment (Settlement)">
          {settlement?.settlement ? (
            <div className="row g-3">
              <div className="col-md-4">
                <FieldLabel label="Status" />
                <ReadOnlyField value={settlement.settlement.status === "CLOSED" ? "Closed" : "In progress"} />
              </div>
              <div className="col-md-4">
                <FieldLabel label="Total Due" />
                <ReadOnlyField value={formatINR(Number(settlement.settlement.total_due) || 0)} />
              </div>
              <div className="col-md-4">
                <FieldLabel label="Collected" />
                <ReadOnlyField value={formatINR(Number(settlement.settlement.total_collected) || 0)} />
              </div>
            </div>
          ) : (
            <div className="text-muted small">
              Not sent to Establishment yet. HR sends it once every department has acted.
            </div>
          )}
        </SectionCard>

        {/* The No-Dues form as submitted */}
        {ndRecord?.details && Object.keys(ndRecord.details).length > 0 && (
          <NoDuesFormSections
            form={ndRecord.details}
            startNumber={4}
            loansOverride={kase.outstanding_loans}
          />
        )}

        {/* Activity */}
        <SectionCard index={3} title="Activity">
          {!activity.length ? (
            <div className="text-muted small">No activity yet.</div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {activity.map((a, i) => (
                <div key={i} className="d-flex gap-3 border-bottom pb-2">
                  <div className="text-muted small flex-shrink-0" style={{ width: 150 }}>
                    {a.acted_at ? formatDateTime(a.acted_at) : "—"}
                  </div>
                  <div className="small">
                    <div className="fw-semibold">
                      {ACTION_LABELS[String(a.action).toUpperCase()] || a.action}
                      {a.role && !["HR", "AM", "DY_HOD", "HOD", "DIRECTOR", "MD"].includes(String(a.role)) ? ` — ${a.role}` : ""}
                    </div>
                    <div className="text-muted">
                      {a.approver_name || a.approver_emp_id}
                      {a.comments ? ` · ${a.comments}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
};

export default NoDuesCaseDetail;
